import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {DEFAULT_API_PATHS, SERVICE_KOPA} from '@shared/config';
import {
  KopaDescriptionItem,
  KopaExtension,
  RawKopaCode
} from '@shared/types/codes';
import useKopaCodeSets from '@common/hooks/useKopaCodeSets';
import {KOPA_CODE} from '@shared/ducks/codes/constants';
import {EMPTY_ARRAY} from '@shared/ducks/constants';

const KopaCodesContext = React.createContext<KopaCodesProviderData>({
  collectedKopaCodes: {},
  isLoading: false,
  getKopaCodeLabel: () => '',
  getLabelFromKopaCode: () => '',
  getKopaCode: () => null,
  getKopaCodeExtensions: () => null,
  getKopaCodeExtensionByType: () => null,
  getAllKopaCodeIdsInCodeset: () => null,
  getAllKopaCodesInCodeset: () => null,
  getCodesWhereExtensionHasValue: () => null
});

type Props = {
  children: ReactNode;
  apiUrl: string;
  kopaCodesets: KOPA_CODE[];
};

export type CollectedKopaCodes = {
  [key in KOPA_CODE]?: RawKopaCode[] | null;
};

export type KopaCodesProviderData = {
  collectedKopaCodes: CollectedKopaCodes;
  isLoading: boolean;
  getKopaCodeLabel: (
    codeset: KOPA_CODE,
    value: string,
    locale: string
  ) => string;
  getLabelFromKopaCode: (
    code: RawKopaCode,
    locale: string,
    fallback?: string
  ) => string;
  getKopaCode: (codeset: KOPA_CODE, value: string) => RawKopaCode | null;
  getKopaCodeExtensions: (
    codeset: KOPA_CODE,
    value: string
  ) => KopaExtension[] | null;
  getKopaCodeExtensionByType: (
    codeset: KOPA_CODE,
    value: string,
    extension: string
  ) => KopaExtension | null;
  getAllKopaCodeIdsInCodeset: (codeset: KOPA_CODE) => string[] | null;
  getAllKopaCodesInCodeset: (codeset: KOPA_CODE) => RawKopaCode[] | null;
  getCodesWhereExtensionHasValue: (
    codeset: KOPA_CODE,
    extensionType: string,
    extensionValue: string
  ) => RawKopaCode[] | null;
};

const DEFAULT_KOPA_CODES_API_PATH = DEFAULT_API_PATHS[SERVICE_KOPA];

const KopaCodesProvider = ({children, apiUrl, kopaCodesets}: Props) => {
  const {
    kopaCodeSets: allKopaCodeSets,
    isLoading: areKopaCodeSetsLoading,
    fetchKopaCodeSets: fetchKopaCodeSets
  } = useKopaCodeSets(apiUrl || DEFAULT_KOPA_CODES_API_PATH, kopaCodesets);

  const [collectedKopaCodes, setCollectedKopaCodes] =
    useState<CollectedKopaCodes>({});

  const getKopaCode = useCallback(
    (codeset: KOPA_CODE, value: string): RawKopaCode | null => {
      const codesetCodes = collectedKopaCodes[codeset];
      const code = (codesetCodes || EMPTY_ARRAY).find(
        (x: RawKopaCode) => x.tunnus == value
      );
      if (!code) {
        return null;
      }
      return code;
    },
    [collectedKopaCodes]
  );

  const getLabelFromKopaCode = useCallback(
    (code: RawKopaCode, locale: string, fallback = ''): string => {
      if (!code) {
        return fallback;
      }
      const foundDescription = (code?.selite || EMPTY_ARRAY).find(
        (val: KopaDescriptionItem) => val?.kielikoodi === locale
      );
      if (!foundDescription) {
        // if can't find results in given locale, take first as fallback
        return code?.selite?.length ? code.selite[0]?.teksti : fallback;
      }
      return foundDescription?.teksti || fallback;
    },
    []
  );

  const getKopaCodeLabel = useCallback(
    (codeset: KOPA_CODE, value: string, locale: string): string => {
      const code = getKopaCode(codeset, value);
      if (!code) {
        return value;
      }
      return getLabelFromKopaCode(code, locale, value);
    },
    [getKopaCode, getLabelFromKopaCode]
  );

  const getKopaCodeExtensions = useCallback(
    (codeset: KOPA_CODE, value: string): KopaExtension[] | null => {
      const code = getKopaCode(codeset, value);
      if (!code) {
        return null;
      }
      return code?.laajennus || EMPTY_ARRAY;
    },
    [getKopaCode]
  );
  const getKopaCodeExtensionByType = useCallback(
    (
      codeset: KOPA_CODE,
      value: string,
      extension: string
    ): KopaExtension | null => {
      const extensions = getKopaCodeExtensions(codeset, value);
      if (!extensions?.length) {
        return null;
      }
      return extensions.find(ext => ext.tyyppi === extension) ?? null;
    },
    [getKopaCodeExtensions]
  );

  /**
   * Generic filtering function for providing only matching codes, for example, to return only codes where some "laajennus" has some specific value.
   * @example getMatchingCodes(KOPA_CODE.COMPANY_FORM, code => code?.laajennus?.[0]?.tyyppi==='YlaTasoKoodi' && code?.laajennus?.[0]?.arvo==='SSSSS')
   *
   * @param codeset codeset to use where to get matching codes
   * @param matchingFunction matcher to filter through all codes from codeset
   * @returns matching codes
   */
  const getMatchingCodes = useCallback(
    (
      codeset: KOPA_CODE,
      matchingFunction: (code: RawKopaCode) => boolean
    ): RawKopaCode[] | null => {
      const codesetCodes = collectedKopaCodes?.[codeset];
      if (!codesetCodes) {
        return null;
      }
      return codesetCodes.filter(matchingFunction);
    },
    [collectedKopaCodes]
  );

  /**
   * Filtering function for most typical cases, providing only matching codes where some code has speficic extension and specific value.
   * @example //get codes where extension of type "YlaTasoKoodi" have value "SSSSS":
   * getCodesWhereExtensionHasValue(KOPA_CODE.COMPANY_FORM, 'YlaTasoKoodi', 'SSSSS')
   *
   * @param codeset codeset to use where to get matching codes
   * @param extensionType to match, code must have this extension type
   * @param extensionValue to match, matching type extension must have this value
   * @returns only matching codes
   */
  const getCodesWhereExtensionHasValue = useCallback(
    (
      codeset: KOPA_CODE,
      extensionType: string,
      extensionValue: string
    ): RawKopaCode[] | null => {
      const matching = getMatchingCodes(codeset, code => {
        return (code?.laajennus || EMPTY_ARRAY).some(
          extension =>
            extension?.tyyppi === extensionType &&
            extension?.arvo === extensionValue
        );
      });
      return matching;
    },
    [getMatchingCodes]
  );

  const getAllKopaCodesInCodeset = useCallback(
    (codeset: KOPA_CODE): RawKopaCode[] | null => {
      const codesetCodes = collectedKopaCodes?.[codeset];
      if (!codesetCodes) {
        return null;
      }
      return codesetCodes;
    },
    [collectedKopaCodes]
  );

  const getAllKopaCodeIdsInCodeset = useCallback(
    (codeset: KOPA_CODE): string[] | null => {
      const codesetCodes = getAllKopaCodesInCodeset(codeset);

      if (!codesetCodes) {
        return null;
      }
      return codesetCodes.map(codeObject => codeObject?.tunnus as string);
    },
    [getAllKopaCodesInCodeset]
  );

  const kopaCodesProviderData: KopaCodesProviderData = useMemo(() => {
    return {
      collectedKopaCodes,
      isLoading: areKopaCodeSetsLoading,
      getKopaCodeLabel,
      getKopaCode,
      getKopaCodeExtensions,
      getKopaCodeExtensionByType,
      getAllKopaCodeIdsInCodeset,
      getAllKopaCodesInCodeset,
      getCodesWhereExtensionHasValue,
      getLabelFromKopaCode
    };
  }, [
    collectedKopaCodes,
    areKopaCodeSetsLoading,
    getKopaCode,
    getKopaCodeLabel,
    getKopaCodeExtensions,
    getKopaCodeExtensionByType,
    getAllKopaCodeIdsInCodeset,
    getAllKopaCodesInCodeset,
    getCodesWhereExtensionHasValue,
    getLabelFromKopaCode
  ]);

  useEffect(() => {
    allKopaCodeSets?.forEach(cs => {
      setCollectedKopaCodes(codes => {
        const newCodes = {...codes};
        newCodes[cs.name as KOPA_CODE] = cs.codes;
        return newCodes;
      });
    });
  }, [allKopaCodeSets]);

  useEffect(() => {
    fetchKopaCodeSets();
  }, [kopaCodesets, fetchKopaCodeSets]);

  return (
    <KopaCodesContext.Provider value={kopaCodesProviderData}>
      {children}
    </KopaCodesContext.Provider>
  );
};

export {KopaCodesProvider as default, KopaCodesContext};
